// src/pages/Team/PendingSignoffs.jsx
// STATUS: active | UPDATED: 2026-09-17
// LOG:
//   2026-09-17  The criteria a manager is actually judging against, from
//               Jazz's Trainer Edition, plus his own answer on where the bar
//               sits. Before this a manager was asked whether somebody was
//               ready with nothing whatsoever to compare them to.
//   2026-08-29  New. The manager's half of sign-off.
//
// The queue a manager opens the product for.
//
// Until now a manager had no reason to sign in. They could look at a roster and
// a progress bar, which is a report, not a job. This is the job: somebody has
// passed the knowledge check and is asking to be watched doing it on the floor.
//
// Approving is a real decision, so the two outcomes are named for what they
// mean rather than yes and no. "Signed off" says this person can do it in front
// of a customer. "Another rep" says not yet, and deliberately leaves them at
// check_passed rather than knocking them back to in_progress, because asking to
// be assessed should never cost somebody progress.
//
// RLS decides what shows up here. signoffs_manager_select is
// current_user_manages(user_id), so a manager sees their floor and nobody
// else's, and this component does not check a role anywhere.

import { useCallback, useEffect, useState } from 'react'
import {
  Box, Flex, HStack, VStack, Text, Button, Icon, Spinner, Textarea, Alert, AlertIcon, useToast,
} from '@chakra-ui/react'
import { ClipboardCheck, Check } from 'lucide-react'
import { listSignoffs, decideSignoff } from '../../lib/signoffs'
import { displayNameFor } from '../../lib/names.js'
import { STEPS } from '../../content/steps'
import { getManagerLayerForStep } from '../../content/manager'

function stepTitle(n) {
  const s = STEPS.find((x) => x.number === n)
  return s ? `Step ${n}, ${s.title}` : `Step ${n}`
}

function Row({ item, person, onDecided }) {
  const [busy, setBusy] = useState(null)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState(null)
  const toast = useToast()

  const decide = async (status) => {
    if (busy) return
    setError(null)
    setBusy(status)
    const res = await decideSignoff(item.id, status, notes)
    setBusy(null)
    if (!res.ok) {
      setError(res.error || 'Could not record that.')
      return
    }
    toast({
      title:
        status === 'approved'
          ? `${person} is signed off on ${stepTitle(item.stepNumber)}.`
          : `Sent back to ${person} for another rep.`,
      status: status === 'approved' ? 'success' : 'info',
      duration: 4000,
    })
    onDecided()
  }

  const criteria = getManagerLayerForStep(item.stepNumber)?.signoffCriteria || []

  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" p={{ base: 4, md: 5 }}>
      <Text fontWeight={600} letterSpacing="-0.01em">{person}</Text>
      <Text fontSize="body-sm" color="inkMuted" mt={0.5}>
        Passed the check on {stepTitle(item.stepNumber)} and is asking to be signed off.
      </Text>

      {/* The standard, in Jazz's words, from the Trainer Edition. A manager
          asked to judge readiness with nothing to judge against will either
          rubber stamp it or invent their own bar, and both of those quietly
          break the thing sign-off exists to guarantee. */}
      {criteria.length > 0 && (
        <Box mt={4}>
          <Text textStyle="eyebrow" color="inkDim" mb={2}>
            What you should have seen
          </Text>
          <VStack align="stretch" spacing={1.5}>
            {criteria.map((c) => (
              <HStack key={c} align="flex-start" spacing={2.5}>
                <Box color="inkDim" mt="3px" flexShrink={0}>
                  <Check size={14} />
                </Box>
                <Text fontSize="body-sm" lineHeight={1.5}>
                  {c}
                </Text>
              </HStack>
            ))}
          </VStack>
          <Text fontSize="sm" color="inkDim" mt={2.5}>
            Jazz on where the bar sits. Once clean with a real customer, three times, a role play at the
            desk or your call in the moment. It is usually all of the above, and it can be in the moment.
          </Text>
        </Box>
      )}

      {error && (
        <Alert status="error" borderRadius="md" fontSize="sm" mt={3}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="What you saw, or what to work on. Optional."
        rows={2}
        resize="none"
        fontSize="body-sm"
        mt={3}
        maxLength={500}
      />

      <HStack spacing={2} mt={3} flexWrap="wrap">
        <Button size="sm" minH="40px" px={4} isLoading={busy === 'approved'} onClick={() => decide('approved')}>
          Signed off
        </Button>
        <Button
          size="sm"
          minH="40px"
          px={4}
          variant="outline"
          isLoading={busy === 'redo'}
          onClick={() => decide('redo')}
        >
          Another rep
        </Button>
      </HStack>
    </Box>
  )
}

export default function PendingSignoffs({ members }) {
  const [state, setState] = useState({ loading: true, items: [] })

  const load = useCallback(async () => {
    const res = await listSignoffs()
    setState({
      loading: false,
      items: res.ok ? res.signoffs.filter((s) => s.status === 'requested') : [],
    })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  if (state.loading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner size="md" color="ink" thickness="3px" />
      </Flex>
    )
  }

  // Nothing pending is the normal state, not an empty state worth a whole card.
  // A manager who sees this every day should be able to tell in one glance.
  if (state.items.length === 0) {
    return (
      <HStack bg="surface" borderRadius="card" px={5} py={4} spacing={3}>
        <Icon as={ClipboardCheck} boxSize={4} color="inkMuted" />
        <Text fontSize="body-sm" color="inkMuted">
          No sign-offs waiting. They appear here when somebody passes a check and asks to be assessed.
        </Text>
      </HStack>
    )
  }

  const nameFor = (userId) => {
    const m = (members || []).find((x) => x.id === userId)
    return m ? displayNameFor(m, { email: m.email }) : 'A team member'
  }

  return (
    <VStack align="stretch" spacing={3}>
      <Text textStyle="eyebrow" color="accent.500">
        {state.items.length} waiting on you
      </Text>
      {state.items.map((item) => (
        <Row key={item.id} item={item} person={nameFor(item.user_id)} onDecided={load} />
      ))}
    </VStack>
  )
}
